/*
  Consultation modal (sections/consult-modal.liquid). Plain ES5 for old in-app WebViews.
  Page and enabled checks happen in Liquid; this file handles when it opens,
  how often, the sound and closing.

  - data-delay: seconds, no upper limit. data-delay-mode "idle" restarts the count
    on every scroll, click, key or touch; "page" counts from page load.
  - data-frequency: "always" | "session" | "day", recorded when the visitor closes
    it or clicks the button.
  - Leaving the tab or minimising the window counts as inactivity on our site: the
    timer keeps running and the modal opens (with the sound) while the visitor is
    away, to call them back. Activity in other tabs never reaches this page.
  - Waits while a cookie banner is up, the cart drawer is open or the membership
    modal is up.
  - Sound: browsers block audio until the visitor has clicked or typed, so the
    AudioContext is unlocked on the first such gesture. Before that it asks the
    browser to resume and then tries a plain <audio>; most browsers refuse both
    (scrolling and mouse moves don't count), so it usually opens silently.
*/
(function () {
  'use strict';

  var SEEN_KEY = 'nooriaConsultModalSeen';

  function storageGet(store, key) {
    try { return window[store].getItem(key); } catch (e) { return null; }
  }

  function storageSet(store, key, value) {
    try { window[store].setItem(key, value); } catch (e) {}
  }

  function alreadySeen(frequency) {
    if (frequency === 'session') return storageGet('sessionStorage', SEEN_KEY) === '1';
    if (frequency === 'day') {
      var last = storageGet('localStorage', SEEN_KEY);
      return !!last && new Date(last).toDateString() === new Date().toDateString();
    }
    return false;
  }

  function markSeen(frequency) {
    if (frequency === 'session') storageSet('sessionStorage', SEEN_KEY, '1');
    if (frequency === 'day') storageSet('localStorage', SEEN_KEY, new Date().toISOString());
  }

  // Sound: an uploaded file if set, otherwise a soft two-note chime.
  function createSound(modal) {
    if (modal.getAttribute('data-sound') !== 'true') return null;
    var Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;

    var url = modal.getAttribute('data-sound-url');
    var volume = parseInt(modal.getAttribute('data-sound-volume'), 10);
    volume = isNaN(volume) ? 0.5 : Math.max(0, Math.min(100, volume)) / 100;
    var ctx = null;
    var buffer = null;
    // Download the file right away, so it is ready when the modal opens; decoding needs the context.
    var fileData = url ? fetch(url).then(function (res) {
      if (!res.ok) throw new Error('sound ' + res.status);
      return res.arrayBuffer();
    }) : null;
    var decoding = null;

    function context() {
      if (!ctx) {
        try { ctx = new Ctx(); } catch (e) { return null; }
      }
      if (fileData && !decoding) {
        decoding = fileData.then(function (data) {
          return new Promise(function (resolve, reject) { ctx.decodeAudioData(data, resolve, reject); });
        }).then(function (decoded) { buffer = decoded; return decoded; });
        decoding.catch(function () {});
      }
      return ctx;
    }

    function unlock() {
      var c = context();
      if (c && c.state === 'suspended') c.resume();
    }

    var gestures = ['pointerdown', 'keydown', 'touchend'];
    for (var i = 0; i < gestures.length; i++) {
      document.addEventListener(gestures[i], unlock, { passive: true, capture: true });
    }

    function chime(c) {
      var now = c.currentTime;
      var notes = [880, 1318.5]; // A5, E6
      for (var n = 0; n < notes.length; n++) {
        var start = now + n * 0.14;
        var osc = c.createOscillator();
        var gain = c.createGain();
        osc.type = 'sine';
        osc.frequency.value = notes[n];
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.25 * volume, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.9);
        osc.connect(gain);
        gain.connect(c.destination);
        osc.start(start);
        osc.stop(start + 0.95);
      }
    }

    function playBuffer(c) {
      var source = c.createBufferSource();
      var gain = c.createGain();
      gain.gain.value = volume;
      source.buffer = buffer;
      source.connect(gain);
      gain.connect(c.destination);
      source.start();
    }

    function playAudioElement() {
      // A plain <audio> may still be allowed (Chrome lets sites the visitor often plays media on autoplay).
      if (!url) return;
      try {
        var audio = new Audio(url);
        audio.volume = volume;
        var attempt = audio.play();
        if (attempt && attempt.catch) attempt.catch(function () {});
      } catch (e) {}
    }

    return function play() {
      if (volume === 0) return;
      var c = context();
      if (!c) return;
      if (c.state !== 'running') {
        // No click or key press on this page yet. A fresh context starts "suspended" even
        // when the browser would allow it (a click on the previous page of the site, a site
        // the visitor often plays media on), so ask to resume; if it stays locked, try <audio>.
        var settled = false;
        var giveUp = setTimeout(function () {
          if (settled) return;
          settled = true;
          playAudioElement();
        }, 300);
        var resumed = c.resume && c.resume();
        if (resumed && resumed.then) {
          resumed.then(function () {
            if (settled || c.state !== 'running') return;
            settled = true;
            clearTimeout(giveUp);
            playUnlocked(c);
          }, function () {});
        }
        return;
      }
      playUnlocked(c);
    };

    function playUnlocked(c) {
      if (buffer) return playBuffer(c);
      if (!decoding) return chime(c);
      // File still loading: wait up to 2 s for it, then fall back to the chime.
      var done = false;
      var fallback = setTimeout(function () { if (!done) { done = true; chime(c); } }, 2000);
      decoding.then(function () {
        if (done) return;
        done = true;
        clearTimeout(fallback);
        playBuffer(c);
      }, function () {
        if (done) return;
        done = true;
        clearTimeout(fallback);
        chime(c);
      });
    }
  }

  function init() {
    var modal = document.getElementById('consultModal');
    if (!modal) return;

    var win = modal.querySelector('.consult-modal__window');
    var cta = modal.querySelector('[data-consult-cta]');
    var designMode = modal.getAttribute('data-design-mode') === 'true';
    var frequency = modal.getAttribute('data-frequency') || 'always';
    var delay = parseFloat(modal.getAttribute('data-delay'));
    delay = isNaN(delay) || delay < 0 ? 0 : delay;
    var idleMode = modal.getAttribute('data-delay-mode') !== 'page';
    var playSound = designMode ? null : createSound(modal);
    var isOpen = false;
    var lastFocus = null;

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocus = document.activeElement;
      modal.hidden = false;
      document.documentElement.classList.add('consult-modal-locked');
      // Next frame so the transition runs from the hidden state.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () { modal.classList.add('is-open'); });
      });
      try { win.focus({ preventScroll: true }); } catch (e) { win.focus(); }
      if (playSound) playSound();
      if (typeof window.clarity === 'function') window.clarity('event', 'consult_modal_shown');
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      markSeen(frequency);
      modal.classList.remove('is-open');
      modal.style.removeProperty('--cm-drag');
      document.documentElement.classList.remove('consult-modal-locked');
      setTimeout(function () { if (!isOpen) modal.hidden = true; }, 300);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    var closers = modal.querySelectorAll('[data-consult-close]');
    for (var i = 0; i < closers.length; i++) closers[i].addEventListener('click', close);

    if (cta) {
      cta.addEventListener('click', function (e) {
        if (cta.getAttribute('href') === '#') e.preventDefault();
        if (typeof window.clarity === 'function') window.clarity('event', 'consult_modal_cta');
        close();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape' || e.key === 'Esc') close();
      if (e.key === 'Tab') {
        // Keep focus inside the dialog.
        var focusable = win.querySelectorAll('a[href], button');
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === win)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    // Bottom sheet: drag down from the top of the sheet to close.
    var dragStart = null;
    win.addEventListener('touchstart', function (e) {
      if (win.scrollTop > 0 || e.touches.length !== 1) return;
      dragStart = e.touches[0].clientY;
    }, { passive: true });
    win.addEventListener('touchmove', function (e) {
      if (dragStart === null) return;
      var dy = e.touches[0].clientY - dragStart;
      if (dy <= 0) return;
      modal.classList.add('is-dragging');
      modal.style.setProperty('--cm-drag', dy + 'px');
    }, { passive: true });
    win.addEventListener('touchend', function (e) {
      if (dragStart === null) return;
      var dy = e.changedTouches[0].clientY - dragStart;
      dragStart = null;
      modal.classList.remove('is-dragging');
      if (dy > 100) close();
      else modal.style.removeProperty('--cm-drag');
    });

    if (designMode) {
      // In the theme editor it opens while the section is selected.
      document.addEventListener('shopify:section:select', function (e) {
        if (modal.closest('#shopify-section-' + e.detail.sectionId)) open();
      });
      document.addEventListener('shopify:section:deselect', function (e) {
        if (modal.closest('#shopify-section-' + e.detail.sectionId)) close();
      });
      return;
    }

    if (alreadySeen(frequency)) return;

    // Cookie banners (TinyCookie, Shopify's own) sit above everything; wait for the choice first.
    function cookieBannerVisible() {
      var banners = document.querySelectorAll('#tinycookie-wrapper, #tinycookie-preferences-wrapper, #shopify-pc__banner');
      for (var b = 0; b < banners.length; b++) {
        if (banners[b].offsetWidth || banners[b].offsetHeight) return true;
      }
      return false;
    }

    function busy() {
      return document.body.classList.contains('page-overlay-cart-on') ||
        !!document.querySelector('.wt-cart__drawer--open, .membership-modal.active') ||
        cookieBannerVisible();
    }

    var timer = null;
    var due = false;

    function tryOpen() {
      if (isOpen || alreadySeen(frequency)) return;
      if (busy()) {
        due = true;
        timer = setTimeout(tryOpen, 2000);
        return;
      }
      stopWatching();
      open();
    }

    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(tryOpen, delay * 1000);
    }

    var activity = ['scroll', 'pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel'];
    var lastActivity = 0;
    function onActivity() {
      if (due || isOpen) return;
      var now = Date.now();
      if (now - lastActivity < 500) return; // pointermove fires a lot
      lastActivity = now;
      schedule();
    }

    function stopWatching() {
      for (var j = 0; j < activity.length; j++) {
        window.removeEventListener(activity[j], onActivity, { passive: true, capture: true });
      }
    }

    if (idleMode && delay > 0) {
      for (var k = 0; k < activity.length; k++) {
        window.addEventListener(activity[k], onActivity, { passive: true, capture: true });
      }
    }
    schedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
